import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from '@material-ui/core/Typography/Typography';
import { red } from '@material-ui/core/colors';
import {
  DetailContext,
  DetailHandle,
  DetailMode,
  EvidenceContext,
  FormAutocomplete,
  FormCheckbox,
  FormContext,
  FormCustomField,
  FormLocalDateField,
  FormLocalDateTimeField,
  FormNumberField,
  FormPanel,
  FormSelect,
  FormTextArea,
  FormTextField,
  TextField,
  eqFilterParams,
  useFormSelector,
  useStaticListSource,
} from '@eas/common-web';
import { useDecisionEsFilePermission } from '@modules/decision/decision-es-file-permission';
import { NOTE_SOURCE } from '@modules/decision/decision-hooked-sources';
import { NoteField } from '@composite/notes/note-field';
import { Redirect } from '@composite/redirect/redirect';
import { StaticDataContext } from '@components/evidence/static-data-provider/static-data-provider';
import { autocompleteLabelMapper } from '@components/form/misc/label-mappers';
import { Decision } from '@models';
import {
  DecisionForm,
  DecisionState,
  EvidenceBrowserUrl,
  UIElement,
} from '@enums';
import { useProcessingTypes } from '../../../dict-processing-type/dict-processing-type-api';
import { useSettlementMethods } from '../../../dict-settlement-method/dict-settlement-method-api';
import { useJudges } from '../../../judge/judge-api';
import { useDecisionPermission } from '../../decision-permission';

const getSyles = () => {
  const citationLeftPart = {
    width: 100,
    marginRight: 5,
  };

  const citationMiddlePart = {
    width: 100,
    marginRight: 5,
    marginLeft: 5,
  };

  const citationRightPart = {
    width: 100,
    marginLeft: 5,
  };

  return { citationLeftPart, citationMiddlePart, citationRightPart };
};

const decisionStateDisabledStyle: React.CSSProperties = {
  lineHeight: '22px',
  height: 22,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

function DecisionStateDisabledComponent({ value }: { value: string | null }) {
  const state = useFormSelector((data: Decision) => data.state);

  return (
    <Typography
      style={{
        ...decisionStateDisabledStyle,
        color: state === DecisionState.CANCELED ? red[500] : undefined,
      }}
    >
      {value ?? ''}
    </Typography>
  );
}

function getSbnuPrefix(form: Decision['form'] | null): string {
  return form === DecisionForm.FINDING
    ? 'N'
    : form === DecisionForm.RESOLUTION
    ? 'U'
    : form === DecisionForm.PLENARY_OPINION
    ? 'ST'
    : '';
}

export function GeneralFields() {
  const { mode, source } = useContext<DetailHandle<Decision>>(DetailContext);
  const { setFieldValue, getFieldValues } =
    useContext<FormContext<Decision>>(FormContext);
  const { hookedSources } = useContext(EvidenceContext);
  const {
    decisionStates,
    decisionForms,
    anonymizationRequests,
    publishInCollection,
  } = useContext(StaticDataContext);

  const { canWrite } = useDecisionPermission(source?.data);
  const decisionEsFilePermission = useDecisionEsFilePermission(source?.data);

  const { citationLeftPart, citationMiddlePart, citationRightPart } =
    getSyles();

  const { parallelCitation, esFile, form } = useFormSelector(
    (data: Decision) => ({
      parallelCitation: data.parallelCitation,
      esFile: data.esFile,
      form: data.form,
    })
  );

  const [sbnuPrefix, setSbnuPrefix] = useState(() =>
    getSbnuPrefix(getFieldValues().form)
  );

  useEffect(() => {
    setSbnuPrefix(getSbnuPrefix(form));
  }, [form]);

  const statesSource = useStaticListSource(decisionStates);
  const formsSource = useStaticListSource(decisionForms);
  const anonymizationRequestsSource = useStaticListSource(
    anonymizationRequests
  );
  const publishInCollectionSource = useStaticListSource(publishInCollection);
  const judges = useJudges();
  const processingTypes = useProcessingTypes();
  const settlementMethods = useSettlementMethods(
    eqFilterParams({ field: 'agenda.id', value: source.data?.esFile?.agenda })
  );

  const noteSource = hookedSources[NOTE_SOURCE]?.source;

  return (
    <>
      <FormPanel
        label={
          <FormattedMessage
            id="ES__DECISIONS__DETAIL__GENERAL_PANEL_TITLE"
            defaultMessage="Rozhodnutí"
          />
        }
      >
        <FormTextField
          name="esFile.number.value"
          label={
            <FormattedMessage
              id="ES__TASKS__DETAIL__FIELD_LABEL__FILE_NUMBER"
              defaultMessage="Spisová značka"
            />
          }
          after={
            esFile?.id && (
              <Redirect url={EvidenceBrowserUrl.ES_FILES_ALL} id={esFile.id} />
            )
          }
          disabled={!canWrite(UIElement.Decision.FILE_NUMBER)}
        />

        <FormAutocomplete
          name="judge"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__JUDGE"
              defaultMessage="Soudce zpravodaj"
            />
          }
          source={judges}
          labelMapper={autocompleteLabelMapper}
          disabled={!canWrite(UIElement.Decision.JUDGE)}
        />

        <FormAutocomplete
          name="esFile.processingType"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__PROCESSING_TYPE"
              defaultMessage="Typ řízení"
            />
          }
          source={processingTypes}
          labelMapper={autocompleteLabelMapper}
          disabled={!decisionEsFilePermission.canWrite('processingType')}
        />

        <FormSelect
          name="esFile.anonymizationRequest"
          source={anonymizationRequestsSource}
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__ANONYMIZATION_REQUEST"
              defaultMessage="Žádost o anonymizaci"
            />
          }
          valueIsId={true}
          disabled={!canWrite(UIElement.Decision.ANONYMIZATION_REQUEST)}
        />
        <FormCheckbox
          name="esFile.anonymize"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__ANONYMIZE"
              defaultMessage="Anonymizovat"
            />
          }
          disabled={!canWrite(UIElement.Decision.ANONYMIZE)}
        />
        <FormSelect
          name="state"
          source={statesSource}
          DisabledComponent={DecisionStateDisabledComponent}
          label={
            <FormattedMessage
              id="ES__DECISINS__DETAIL__FIELD_LABEL__STATE"
              defaultMessage="Stav"
            />
          }
          valueIsId={true}
          disabled={!canWrite(UIElement.Decision.STATE)}
        />
        <FormTextField
          name="number"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__NUMBER"
              defaultMessage="Číslo rozhodnutí"
            />
          }
          disabled={!canWrite(UIElement.Decision.NUMBER)}
        />
        <FormTextField
          name="ecli"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__EUROPEAN_CASE_LAW_ID"
              defaultMessage="Identifikátor evropské judikatury"
            />
          }
          disabled={!canWrite(UIElement.Decision.ECLI)}
        />
        <FormSelect
          name="form"
          source={formsSource}
          notifyChange={() =>
            setSbnuPrefix(getSbnuPrefix(getFieldValues().form))
          }
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__FORM"
              defaultMessage="Forma rozhodnutí"
            />
          }
          valueIsId={true}
          disabled={!canWrite(UIElement.Decision.FORM)}
        />
        <FormAutocomplete
          name="settlementMethods"
          source={settlementMethods}
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__SETTLEMENT_METHODS"
              defaultMessage="Způsoby vyřízení"
            />
          }
          labelMapper={autocompleteLabelMapper}
          multiple={true}
          disabled={!canWrite(UIElement.Decision.SETTLEMENT_METHODS)}
        />
        <FormCheckbox
          name="allowPublishing"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__ALLOW_PUBLISHING"
              defaultMessage="Umožnit zveřejnění"
            />
          }
          disabled={!canWrite(UIElement.Decision.ALLOW_PUBLISHING)}
        />
        <FormCheckbox
          name="takeOverIndexes"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__TAKE_OVER_INDEXES"
              defaultMessage="Převzít indexy"
            />
          }
          disabled={!canWrite(UIElement.Decision.TAKE_OVER_INDEXES)}
        />
        <FormLocalDateField
          name="decisionDate"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__DECISION_DATE"
              defaultMessage="Datum rozhodnutí"
            />
          }
          disabled={!canWrite(UIElement.Decision.DECISION_DATE)}
        />
        <FormLocalDateField
          name="publicationDate"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__PUBLICATION_DATE"
              defaultMessage="Datum vyhlášení"
            />
          }
          disabled={!canWrite(UIElement.Decision.PUBLICATION_DATE)}
        />
        <FormLocalDateField
          name="firstMadeAccessibleDate"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__FIRST_MADE_ACCESSIBLE_DATE"
              defaultMessage="Datum prvního zpřístupnění"
            />
          }
          disabled={!canWrite(UIElement.Decision.FIRST_MADE_ACCESSIBLE_DATE)}
        />
        <FormLocalDateField
          name="lastMadeAccessibleDate"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__LAST_MADE_ACCESSIBLE_DATE"
              defaultMessage="Datum posledního zveřejnění"
            />
          }
          disabled={!canWrite(UIElement.Decision.LAST_MADE_ACCESSIBLE_DATE)}
        />
        <FormSelect
          name="publishInCollection"
          source={publishInCollectionSource}
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__PUBLISH_IN_COLLECTION"
              defaultMessage="Publikovat ve sbírce"
            />
          }
          valueIsId={true}
          disabled={!canWrite(UIElement.Decision.PUBLISH_IN_COLLECTION)}
        />
        <FormCustomField
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__PARALLEL_CITATION_SB"
              defaultMessage="Paralelní citace (Sb.)"
            />
          }
        >
          {mode === DetailMode.VIEW && (
            <Typography>{`${parallelCitation?.sbNumber ?? ''}/${
              parallelCitation?.sbYear ?? ''
            } Sb.`}</Typography>
          )}
          {mode !== DetailMode.VIEW && (
            <>
              <TextField
                style={citationLeftPart}
                value={parallelCitation?.sbNumber}
                onChange={(value) =>
                  setFieldValue('parallelCitation.sbNumber', value)
                }
                disabled={!canWrite(UIElement.Decision.PARALLEL_CITATION_SB)}
              />
              /
              <TextField
                style={citationMiddlePart}
                value={parallelCitation?.sbYear}
                onChange={(value) =>
                  setFieldValue('parallelCitation.sbYear', value)
                }
                disabled={!canWrite(UIElement.Decision.PARALLEL_CITATION_SB)}
              />
              Sb.
            </>
          )}
        </FormCustomField>
        <FormCustomField
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__PARALLEL_CITATION_SBNU"
              defaultMessage="Paralelní citace (SbNU)"
            />
          }
        >
          {mode === DetailMode.VIEW && (
            <Typography>
              {form === DecisionForm.PLENARY_OPINION &&
              parallelCitation?.sbnuValue &&
              !/^ST(?:\s|$)/.test(parallelCitation.sbnuValue)
                ? `ST ${parallelCitation.sbnuValue}`
                : parallelCitation?.sbnuValue ?? ''}
            </Typography>
          )}
          {mode !== DetailMode.VIEW && (
            <>
              <TextField
                style={{ ...citationLeftPart, width: 40 }}
                disabled
                value={sbnuPrefix}
              />
              <TextField
                style={citationLeftPart}
                value={parallelCitation?.sbnuNumber}
                onChange={(value) =>
                  setFieldValue('parallelCitation.sbnuNumber', value)
                }
                disabled={!canWrite(UIElement.Decision.PARALLEL_CITATION_SBNU)}
              />
              /
              <TextField
                style={citationMiddlePart}
                value={parallelCitation?.sbnuVolume}
                onChange={(value) =>
                  setFieldValue('parallelCitation.sbnuVolume', value)
                }
                disabled={!canWrite(UIElement.Decision.PARALLEL_CITATION_SBNU)}
              />
              SbNU
              <TextField
                style={citationRightPart}
                value={parallelCitation?.sbnuPage}
                onChange={(value) =>
                  setFieldValue('parallelCitation.sbnuPage', value)
                }
                disabled={!canWrite(UIElement.Decision.PARALLEL_CITATION_SBNU)}
              />
            </>
          )}
        </FormCustomField>
        <FormTextField
          name="popularName"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__POPULAR_NAME"
              defaultMessage="Populární název"
            />
          }
          disabled={!canWrite(UIElement.Decision.POPULAR_NAME)}
        />
        <FormNumberField
          name="importance"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__IMPORTANCE"
              defaultMessage="Význam"
            />
          }
          disabled={!canWrite(UIElement.Decision.IMPORTANCE)}
        />
        <FormAutocomplete
          name="differentOpinion"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__DIFFERENT_OPINION"
              defaultMessage="Odlišné stanovisko"
            />
          }
          source={judges}
          labelMapper={autocompleteLabelMapper}
          multiple={true}
          disabled={!canWrite(UIElement.Decision.DIFFERENT_OPINION)}
        />
        <FormTextArea
          name="note"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__NOTE"
              defaultMessage="Externí poznámka"
            />
          }
          disabled={!canWrite(UIElement.Decision.NOTE)}
        />
        <FormTextField
          name="language"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__LANGUAGE"
              defaultMessage="Jazyk rozhodnutí"
            />
          }
          disabled={!canWrite(UIElement.Decision.LANGUAGE)}
        />
        <FormCheckbox
          name="excludedFromNalus"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__EXCLUDED_FROM_NALUS"
              defaultMessage="Vyloučeno z NALUSu"
            />
          }
          disabled={!canWrite(UIElement.Decision.EXCLUDED_FROM_NALUS)}
        />
        <FormLocalDateTimeField
          name="updated"
          disabled
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__UPDATED"
              defaultMessage="Datum změny"
            />
          }
        />
      </FormPanel>
      <NoteField
        disabled={
          !canWrite(UIElement.Decision.NOTES) || mode !== DetailMode.VIEW
        }
        source={noteSource}
        label={
          <FormattedMessage
            id="ES__INTERNAL_NOTES__PANEL_TITLE"
            defaultMessage="Interní poznámky"
          />
        }
      />
    </>
  );
}
